import get from 'lodash/get'
import { dateFns as moment } from '@pcln/date-fns-utils'
import findIndex from 'lodash/findIndex'
import { differenceInDays } from 'date-fns/differenceInDays'
import Cookies from 'js-cookie'

import { priceGuidanceTopDestinations } from './price-guidance-constants'
import { translateSearchRequest } from '../../listings-client/util/translateSearchRequest'
import { PassengerNumbers } from '../../common/types'
import { MIN_ADULTS, MAX_CHILDREN, MAX_PASSENGER } from '../constants/passengerConstants'
import { getPackageType } from './getPackageType'

export function getIsShelfEligible(
  hasUSDestinationsOnly,
  tripType,
  hasExpressDeal,
  cabinClass,
  isDesktop,
  hasUpsellOptions
) {
  return (
    hasUSDestinationsOnly &&
    tripType === 'OW' &&
    !hasExpressDeal &&
    cabinClass === 'ECO' &&
    isDesktop &&
    hasUpsellOptions
  )
}

function getNumDaysToDeparture(departDateString) {
  const today = new Date()
  const departDate = new Date(departDateString)
  return differenceInDays(departDate, today)
}

export function getApGroup(searchRequest) {
  const departureDate = get(searchRequest.slices, [0, 'travelDateAsmm/dd/yyyy'], null)
  const daysOut = getNumDaysToDeparture(departureDate)
  if (daysOut <= 7) {
    return '0-7 days'
  } else if (daysOut <= 14) {
    return '8-14 days'
  } else if (daysOut <= 21) {
    return '15-21 days'
  } else if (daysOut <= 28) {
    return '22-28 days'
  }
  return '29+ days'
}

export function getCityCode(searchRequest, airportKey) {
  const airportCode = get(searchRequest, ['slices', 0, airportKey])
  return get(searchRequest, ['airports', airportCode, 'cityCode'])
}

export function findPriceGuidance(searchRequest) {
  const origin = getCityCode(searchRequest, 'originAirport')
  const destination = getCityCode(searchRequest, 'destinationAirport')
  const apGroup = getApGroup(searchRequest)
  const key = `${origin}-${destination}`
  return get(priceGuidanceTopDestinations, [key, apGroup], null)
}

export function isEconomy(searchRequest) {
  return get(searchRequest, 'params.cabin-class') === 'ECO'
}

export function getHasUpsellOptions(listings) {
  const upsellIndex = findIndex(listings, (o) => o.upsellOptions && o.upsellOptions.length > 0)
  return upsellIndex !== -1
}

export function generatePhoneBookInput(itineraryDetails, pclnBootstrapData, isMobile, isDomesticOnly) {
  const formattedPurchaseWindow = moment(new Date()).format().split('T')[0]

  const translatedRequest = translateSearchRequest(pclnBootstrapData?.searchRequest)

  const purchaseWindow = moment(translatedRequest?.travelDate).diff(formattedPurchaseWindow, 'days')

  const refid = pclnBootstrapData?.referral?.sourceId + pclnBootstrapData?.referral?.id

  const userCGUID = pclnBootstrapData?.webstats?.clientGUID

  const requestId = pclnBootstrapData?.webstats?.clientRequestId

  const airportsArray = Object.values(translatedRequest?.airports)

  const getLengthofStay = moment(translatedRequest?.returnDate).diff(translatedRequest?.travelDate, 'days')

  let marketingAirline = []
  let operatingAirline = []

  itineraryDetails?.slices?.forEach((sliceObj) => {
    sliceObj?.segments?.forEach((segmentObj) => {
      marketingAirline?.push(segmentObj?.marketingAirline)
      if (segmentObj?.operatingAirline) {
        operatingAirline?.push(segmentObj?.operatingAirline)
      }
    })
  })

  const phoneBookInput = {
    requestId: requestId,
    cguid: userCGUID,
    refid: refid,
    clientDevice: isMobile ? 'MOBILE' : 'DESKTOP',
    isTestNumber: false,
    advancedPurchaseWindow: purchaseWindow,
    currencyCode: 'USD',
    origCountryCode: airportsArray[0]?.countryCode,
    destCountryCode: airportsArray[1]?.countryCode,
    originAirport: translatedRequest?.departingFrom?.airportCode,
    clientCountryCode: pclnBootstrapData?.itinDetails?.pointOfSale?.requestSourceCountry,
    clientIp: pclnBootstrapData?.webstats?.resolvedClientIP,
    destinationAirport: translatedRequest?.returningFrom?.airportCode,
    isInternational: !isDomesticOnly,
    passengers: itineraryDetails?.numberInParty,
    travelStartDate: translatedRequest?.travelDate,
    travelEndDate: translatedRequest?.returnDate,
    searchOptions: {
      cabinClass: translatedRequest?.cabinClass || 'ECO',
    },
    pageType: 'DETAILS',
    tripType: translatedRequest?.tripType,
    marketingAirline: marketingAirline,
    operatingAirline: operatingAirline,
  }

  if (translatedRequest.tripType !== 'OW') {
    phoneBookInput.lengthOfStay = getLengthofStay
  }

  return phoneBookInput
}

export async function generatePhoneBookPackagesInput(
  itineraryDetails,
  pclnBootstrapData,
  isMobile,
  airDetailsState,
  params
) {
  const translatedRequest = translateSearchRequest(pclnBootstrapData?.searchRequest)

  const { departingFrom, returningFrom, travelDate, returnDate } = translatedRequest

  const {
    referral = {},
    partner = {},
    webstats = {},
    isDomestic = true,
    bundleComponents,
    isSignedIn,
  } = pclnBootstrapData

  const { sourceId = '', id = '', clickId = '' } = referral

  const refid = sourceId + id

  const userCGUID = webstats?.clientGUID

  const requestId = webstats?.clientRequestId

  const clientIP = webstats?.resolvedClientIP

  const airportsArray = Object.values(translatedRequest?.airports || {})

  const numChildren = parseInt(params['num-children'] || '0')

  const numAdults = parseInt(params['num-adults'] || '0')
  const cartKey = params['cart-key'] || null

  let marketingAirline = []
  let operatingAirline = []

  itineraryDetails?.slices.forEach((sliceObj) => {
    sliceObj.segments.forEach((segmentObj) => {
      marketingAirline.push(segmentObj?.marketingAirline)
      if (segmentObj?.operatingAirline) {
        operatingAirline.push(segmentObj.operatingAirline)
      }
    })
  })

  const placementID = 'Flight_Details'
  const clientDevice = isMobile ? 'MOBILE' : 'DESKTOP'

  const originSource = departingFrom || airportsArray[0]
  const destinationSource = returningFrom || airportsArray[1]

  const origin = {
    airportCode: originSource?.airportCode,
    cityID: originSource?.cityId?.toString(),
    country: originSource?.countryName,
    countryCode: originSource?.countryCode,
    cityName: originSource?.cityName,
    stateCode: originSource?.stateCode,
    type: 'AIRPORT',
  }

  const destination = {
    airportCode: destinationSource?.airportCode,
    cityID: destinationSource?.cityId?.toString(),
    country: destinationSource?.countryName,
    countryCode: destinationSource?.countryCode,
    cityName: destinationSource?.cityName,
    stateCode: destinationSource?.stateCode,
    type: 'AIRPORT',
  }

  const packageType = getPackageType(bundleComponents)

  return {
    cartID: cartKey,
    cguid: userCGUID,
    clientDevice: clientDevice,
    placementID: placementID,
    clientIP: clientIP,
    pageURL: window.location.href,
    isSignedIn: isSignedIn,
    referral: {
      appCode: clientDevice,
      placementID: placementID,
      cguid: userCGUID,
      clientCountryCode: null,
      clientIP: clientIP,
      clientDevice: clientDevice,
      clientLocale: null,
      currencyCode: itineraryDetails?.price[0]?.currencyCode,
      globalPartnerCode: partner?.globalPartnerCode || 'PCLN',
      plfCode: partner?.plfCode || 'PCLN',
      referralClickID: clickId,
      referralSourceID: sourceId,
      referralID: id,
      refID: refid,
      requestID: requestId,
      userAgent: webstats?.userAgent,
      visitID: webstats?.visitId,
      forterToken: Cookies.get('forterToken'),
    },
    search: {
      packageSearchOptions: {
        adults: numAdults,
        children: numChildren,
        travelStartDate: travelDate,
        travelEndDate: returnDate,
        origin: origin,
        destination: destination,
        packagePrice: airDetailsState?.price[1]?.amount,
        packageType: packageType,
      },
      flightSearchOptions: {
        origin: origin,
        destination: destination,
        isInternational: !isDomestic,
        isSplitTicket: !(airDetailsState?.hasOnlyOneFareBrand || true),
        operatingAirlines: operatingAirline,
        marketingAirlines: marketingAirline,
      },
    },
  }
}

// TODO: Invalid passenger search params are being stripped out already by the search-data-parser.js
// middleware, so this check will only fail because adultsNumber (which will be set 0 as it's null) is less
// than MIN_ADULTS
function validatePassengerNumbers(params) {
  const adultsNumber = Number(params[PassengerNumbers.ADULT] ?? 0)
  const childrenNumber = Number(params[PassengerNumbers.CHILD] ?? 0)
  const lapInfantsNumber = Number(params[PassengerNumbers.LAP_INFANT] ?? 0)
  const passengersNumber = adultsNumber + childrenNumber + lapInfantsNumber

  /** check integer number */
  if (!Number.isInteger(passengersNumber)) {
    return false
  }

  if (adultsNumber < MIN_ADULTS) {
    return false
  }
  if (lapInfantsNumber > adultsNumber) {
    return false
  }
  if (childrenNumber > MAX_CHILDREN) {
    return false
  }
  if (passengersNumber > MAX_PASSENGER) {
    return false
  }
  return true
}
export function validateSearchParams(params) {
  return validatePassengerNumbers(params)
}
